<template>
  <div>
    <div class="tile">
      <div class="form-inline">
        <b-form-input type="month" v-model="period" class="mr-1" @change="load"></b-form-input>
        <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <caption class="text-center text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('reportTitle') }} {{ monthLabel }}
          </caption>
          <thead>
          <tr>
            <th>{{ $t('region') }}</th>
            <th>{{ $t('salesPerson') }}</th>
            <th>1. CallioN</th>
            <th>1*. CallioN*</th>
            <th>2. CallioE</th>
            <th>3. CallioA</th>
            <th>4. CallioU</th>
            <th>{{ $t('totalRevenue') }}</th>
            <th>{{ $t('target') }}</th>
            <th>{{ $t('kpiRate') }}</th>
            <th>{{ $t('note') }}</th>
            <th>{{ $t('trialRegistered') }}</th>
            <th>{{ $t('trialTarget') }}</th>
          </tr>
          </thead>
          <tbody>
          <template v-for="region in regions">
            <tr v-for="(item, index) in region.employees" :key="item.userId">
              <td v-if="index === 0" :rowspan="region.employees.length" class="align-middle font-weight-bold">
                {{ region.office }}
              </td>
              <td class="text-left">{{ item.name }}</td>
              <td class="text-right">{{ item.new | numFormat }}</td>
              <td class="text-right">{{ item.newOther | numFormat }}</td>
              <td class="text-right">{{ item.extend | numFormat }}</td>
              <td class="text-right">{{ item.user | numFormat }}</td>
              <td class="text-right">{{ item.upgrade | numFormat }}</td>
              <td class="text-right">{{ item.total | numFormat }}</td>
              <td class="text-right">{{ item.target | numFormat }}</td>
              <td class="text-right">{{ rate(item.total, item.target) }}</td>
              <td>{{ item.trialTarget > 0 ? $t('probation') : '' }}</td>
              <td class="text-right">{{ item.trialRegistered | numFormat }}</td>
              <td class="text-right">{{ item.trialTarget | numFormat }}</td>
            </tr>
            <tr :key="region.office + '-subtotal'" class="font-weight-bold" style="background-color: #d9edf7;">
              <td colspan="2" class="text-left">{{ $t('subtotal') }} {{ region.office }}</td>
              <td class="text-right">{{ region.subtotal.new | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.newOther | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.extend | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.user | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.upgrade | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.total | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.target | numFormat }}</td>
              <td class="text-right">{{ rate(region.subtotal.total, region.subtotal.target) }}</td>
              <td></td>
              <td class="text-right">{{ region.subtotal.trialRegistered | numFormat }}</td>
              <td class="text-right">{{ region.subtotal.trialTarget | numFormat }}</td>
            </tr>
          </template>
          <tr v-if="items.length" class="font-weight-bold text-danger" style="background-color: #fcf8e3;">
            <td colspan="2" class="text-left">{{ $t('grandTotal') }}</td>
            <td class="text-right">{{ grandTotal.new | numFormat }}</td>
            <td class="text-right">{{ grandTotal.newOther | numFormat }}</td>
            <td class="text-right">{{ grandTotal.extend | numFormat }}</td>
            <td class="text-right">{{ grandTotal.user | numFormat }}</td>
            <td class="text-right">{{ grandTotal.upgrade | numFormat }}</td>
            <td class="text-right">{{ grandTotal.total | numFormat }}</td>
            <td class="text-right">{{ grandTotal.target | numFormat }}</td>
            <td class="text-right">{{ rate(grandTotal.total, grandTotal.target) }}</td>
            <td></td>
            <td class="text-right">{{ grandTotal.trialRegistered | numFormat }}</td>
            <td class="text-right">{{ grandTotal.trialTarget | numFormat }}</td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="13" class="text-danger">{{ $t('noData') }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      period: moment().format('YYYY-MM'),
      items: [],
    }
  },
  computed: {
    monthLabel() {
      return moment(this.period, 'YYYY-MM').format('MM/YYYY');
    },
    regions() {
      let map = {};
      let order = [];
      this.items.forEach(item => {
        let office = item.office || this.$t('unknownRegion');
        if (!map[office]) {
          map[office] = {office, employees: [], subtotal: this.emptyTotal()};
          order.push(office);
        }
        map[office].employees.push(item);
        this.accumulate(map[office].subtotal, item);
      });
      order.forEach(office => {
        map[office].employees.sort((a, b) => b.total - a.total);
      });
      return order.map(office => map[office]);
    },
    grandTotal() {
      let total = this.emptyTotal();
      this.items.forEach(item => this.accumulate(total, item));
      return total;
    }
  },
  methods: {
    load() {
      let [year, month] = this.period.split('-');
      let loader = this.$loading.show();
      this.$service.get('/report/kpi-revenue', {
        params: {month: parseInt(month), year: parseInt(year)}
      }).then(response => {
        this.items = response.data;
      }).finally(() => {
        loader.hide();
      });
    },
    emptyTotal() {
      return {new: 0, newOther: 0, extend: 0, user: 0, upgrade: 0, total: 0, target: 0, trialRegistered: 0, trialTarget: 0};
    },
    accumulate(acc, item) {
      acc.new += item.new;
      acc.newOther += item.newOther;
      acc.extend += item.extend;
      acc.user += item.user;
      acc.upgrade += item.upgrade;
      acc.total += item.total;
      acc.target += item.target;
      acc.trialRegistered += item.trialRegistered;
      acc.trialTarget += item.trialTarget;
    },
    rate(total, target) {
      if (!target) {
        return '-';
      }
      return (total / target * 100).toFixed(2) + '%';
    }
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        reportTitle: 'Revenue report by employee',
        region: 'Region',
        salesPerson: 'Sales person',
        totalRevenue: 'Total revenue',
        target: 'Target',
        kpiRate: 'KPI rate',
        note: 'Note',
        trialRegistered: 'Registered trial customers',
        trialTarget: 'Trial customer target',
        subtotal: 'Subtotal',
        grandTotal: 'Grand total',
        probation: 'Probation',
        unknownRegion: 'Unknown',
        noData: 'No KPI data for this month',
      },
      vi: {
        reportTitle: 'Báo cáo doanh thu theo nhân viên tháng',
        region: 'Khu vực',
        salesPerson: 'Nhân viên kinh doanh',
        totalRevenue: 'Tổng doanh thu',
        target: 'Target',
        kpiRate: 'Tỷ lệ đạt KPI',
        note: 'Ghi chú',
        trialRegistered: 'Khách hàng đăng ký dùng thử',
        trialTarget: 'Target khách hàng dùng thử',
        subtotal: 'Tổng doanh thu',
        grandTotal: 'Cả nước',
        probation: 'Thử việc',
        unknownRegion: 'Chưa xác định',
        noData: 'Chưa có dữ liệu KPI cho tháng này',
      }
    }
  }
}
</script>